import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronDown,
	faTruckPickup,
	faCar,
	faTrash,
	faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import { fetchFichesOuvertes } from "../../store/actions/ficheOuverteActions"
import { detenteurFiche } from "../../hooks/useFicheVerrou"
import { getOpenerId } from "../../utilities/session"
import { setFeuilletage } from "../../store/actions/feuilletageActions"
import moment from "moment"
import { useGroups } from "../../hooks/groups"
import { useRights } from "../../hooks/rights"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { COLORS } from "../../utilities/Constants"
import { HTtoTTC, round100, formatNumber } from "../../utilities/Addons"
import { atelierTable } from "../PDF/AtelierTable"
import {
	deleteAtelier,
	deleteAtelierReset,
	setAtelierSearchResults,
} from "../../store/actions/atelierSearchActions"
import ResultNumCell, { RESULT_ICON_SIZE } from "../others/ResultNumCell"
import AtelierSearchTotal from "./AtelierSearchTotal"
import AtelierSearchMarge from "./AtelierSearchMarge"
import ResultDeleteModal from "../others/ResultDeleteModal"
import { BadgeIssueDepannage } from "./AtelierIssueDepannageV2"

// Résultats de recherche atelier (V2). Page 2/2. Style calqué sur LocationSearch
// (conteneur arrondi + ombre, zebra + hover, tri par colonne). Ouvrir une fiche
// (double-clic) navigue vers `/atelier/fiche/:id` en figeant l'ordre affiché
// dans redux + la liste de feuilletage du domaine (slice `feuilletageInfo`).
const byDate = (a, b) =>
	moment(a.date, "DD-MM-YYYY") - moment(b.date, "DD-MM-YYYY")
// GE-48 : fiche jamais terminée (date vide) rangée après les datées.
const byTermine = (a, b) => {
	const da = a.date_travaux_termines
	const db = b.date_travaux_termines
	if (!da || !db) return !da - !db || byDate(a, b)
	return moment(da, "DD-MM-YYYY") - moment(db, "DD-MM-YYYY") || byDate(a, b)
}
const COMPARATORS = {
	date: byDate,
	termine: byTermine,
	nom: (a, b) =>
		(a.nom || "").localeCompare(b.nom || "", "fr") || byDate(a, b),
	etat: (a, b) =>
		(a.etat || "").localeCompare(b.etat || "", "fr") || byDate(a, b),
	employe: (a, b) =>
		(a.employe || "").localeCompare(b.employe || "", "fr") || byDate(a, b),
	total: (a, b) => Number(a.total) - Number(b.total),
}
const ORDER_BY = {
	date: "date",
	termine: "date_travaux_termines",
	nom: "nom",
	etat: "etat",
	employe: "employe",
	total: "total",
}

const SortableTh = ({ label, colKey, sort, onSort, right }) => {
	const active = sort.key === colKey
	return (
		<th style={right ? tableStyles.thRight : tableStyles.th}>
			<span style={styles.thInner}>
				{label}
				<button
					type="button"
					style={styles.sortBtn(active, sort.asc)}
					onClick={() => onSort(colKey)}
				>
					<FontAwesomeIcon icon={faChevronDown} />
				</button>
			</span>
		</th>
	)
}

const ResultRow = ({
	fiche,
	index,
	isSVP,
	peutSupprimer,
	total,
	isOpen,
	lockedBy,
	onDoubleClick,
	onOpenDep,
	onOpenLoc,
	onDelete,
}) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const stop = (fn) => (e) => {
		e.stopPropagation()
		fn()
	}
	return (
		<tr
			onDoubleClick={onDoubleClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: "pointer",
				backgroundColor: bg,
				transition: "background-color 0.12s",
			}}
		>
			<ResultNumCell index={index} isOpen={isOpen} lockedBy={lockedBy}>
				{fiche.is_client_incomplet && (
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						title="Fiche client incomplète"
						style={{
							color: COLORS.warning,
							fontSize: RESULT_ICON_SIZE,
						}}
					/>
				)}
				{(fiche.stock_non_debite || fiche.piece_a_commander) && (
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						title={
							fiche.stock_non_debite
								? "Stock non débité"
								: "Pièce à commander"
						}
						style={{
							color: COLORS.warning,
							fontSize: RESULT_ICON_SIZE,
						}}
					/>
				)}
			</ResultNumCell>
			<td style={styles.td}>{fiche.date}</td>
			<td style={styles.td}>{fiche.date_travaux_termines || "—"}</td>
			{!isSVP && (
				<td style={styles.td}>
					{fiche.nom}
					<BadgeIssueDepannage issue={fiche.issue_depannage} />
				</td>
			)}
			<td style={styles.td}>{fiche.marque}</td>
			{isSVP && <td style={styles.td}>{fiche.modele}</td>}
			<td style={styles.td}>{fiche.plaques}</td>
			<td style={styles.td}>{fiche.etat}</td>
			<td style={styles.td}>{fiche.employe}</td>
			<td style={{ ...styles.td, textAlign: "right" }}>
				{formatNumber(total)}
			</td>
			<td style={styles.td}>
				<div style={styles.toolsCell}>
					{fiche.dep > 0 && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.text)}
							title="Fiche dépannage"
							onClick={stop(onOpenDep)}
						>
							<FontAwesomeIcon icon={faTruckPickup} />
						</button>
					)}
					{fiche.location > 0 && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.text)}
							title="Fiche location"
							onClick={stop(onOpenLoc)}
						>
							<FontAwesomeIcon icon={faCar} />
						</button>
					)}
					{peutSupprimer && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.danger)}
							title="Supprimer"
							onClick={stop(onDelete)}
						>
							<FontAwesomeIcon icon={faTrash} />
						</button>
					)}
				</div>
			</td>
		</tr>
	)
}

const AtelierResultatsV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	// Boss + admin (GE-69 lot 1) ; le serveur refuse en plus (409) une fiche
	// qui porte de l'argent, du stock débité ou un cycle d'entretien.
	const peutSupprimer = useRights().hasRight("atelier_suppression")
	const {
		atelierFiches,
		criteres,
		deleteAtelier: deleteState,
	} = useSelector((state) => state.atelierSearchInfo)
	const employes = useSelector((state) => state.masterReducer.employes)
	const token = useSelector((state) => state.navInfo.login.token)
	const openAtelierId = useSelector((state) => state.atelierInfo.atelier.id)
	const fichesOuvertes = useSelector(
		(state) => state.fichesOuvertesInfo.fichesOuvertes,
	)

	// Cadenas : nom du détenteur si la fiche est ouverte par une AUTRE session.
	// `detenteurFiche` est LA définition partagée du verrou (elle sert aussi à
	// `useAtelierPermissions` et aux résultats dép/location) — une seule règle,
	// pas de comparaison recopiée à la main.
	const openerId = getOpenerId()
	const lockedBy = (id) => {
		const detenteur = detenteurFiche(fichesOuvertes, "ate", id, openerId)
		return detenteur ? detenteur.user : null
	}

	useEffect(() => {
		dispatch(fetchFichesOuvertes())
	}, [dispatch])

	const [sort, setSort] = useState({ key: "date", asc: true })
	const [openTotal, setOpenTotal] = useState(false)
	const [openMarge, setOpenMarge] = useState(false)
	const [modalDelete, setModalDelete] = useState(null) // {id, title}

	const isSVP = atelierFiches.length > 0 ? atelierFiches[0].svp : false

	// Copie triée — on ne mute jamais le tableau redux.
	const sortedFiches = useMemo(() => {
		const cmp = COMPARATORS[sort.key]
		const dir = sort.asc ? 1 : -1
		return cmp
			? [...atelierFiches].sort((a, b) => dir * cmp(a, b))
			: [...atelierFiches]
	}, [atelierFiches, sort])

	const getTotal = (a) =>
		isSVP
			? round100(HTtoTTC(a.total_achat_ht, a.tva))
			: round100(HTtoTTC(a.total_ht, a.tva))

	const onSort = (key) =>
		setSort((prev) =>
			prev.key === key ? { key, asc: !prev.asc } : { key, asc: true },
		)

	const openFiche = (id) => {
		// Persiste l'ordre trié dans le slice de recherche (on le retrouve au
		// retour), puis fige la liste de feuilletage sur ce MÊME ordre : Page
		// Up/Down de la fiche suit exactement ce que montre le tableau.
		dispatch(setAtelierSearchResults(sortedFiches))
		dispatch(
			setFeuilletage(
				"ate",
				sortedFiches.map((f) => f.id),
				"atelierSearch",
			),
		)
		history.push(`/atelier/fiche/${id}`)
	}

	const doDelete = () => {
		// Le backend DELETE relance la recherche : même dialecte que le GET
		// (query string) — employé/état/type de date en chaînes (il compare à
		// "0"/"-2"), cases en booléens. Sans critères V2 (résultats de la page
		// legacy), pas de relance : un {} plantait la recherche (KeyError) après
		// la suppression ; la ligne quitte alors la liste (reducer).
		const search = criteres
			? {
					nom: criteres.nom || "",
					marque: criteres.marque || "",
					plaques: criteres.plaques || "",
					employe: String(criteres.employe ?? "0"),
					etat: String(criteres.etat ?? "-2"),
					dateT: String(criteres.dateT ?? "0"),
					dateS: criteres.dateS || "",
					dateE: criteres.dateE || "",
					svp: !!criteres.svp,
					boss: !!criteres.boss,
				}
			: undefined
		dispatch(
			deleteAtelier(
				JSON.stringify({
					id: modalDelete.id,
					search,
					orderBy: ORDER_BY[sort.key] || "",
				}),
				true,
			),
		)
	}
	// Purge d'abord le slice : un état resté d'un flux interrompu (page quittée
	// en pleine suppression) rouvrirait le modal sur un ✓ ou une erreur périmés.
	const openDelete = (fiche) => {
		dispatch(deleteAtelierReset())
		setModalDelete(fiche)
	}
	const closeDelete = () => {
		setModalDelete(null)
		dispatch(deleteAtelierReset())
	}

	return (
		<div style={gs.page}>
			<div style={styles.shell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Atelier</div>
						<div style={gs.title}>
							Résultats ({atelierFiches.length})
						</div>
					</div>
					<div style={styles.headerActions}>
						{atelierFiches.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => atelierTable(sortedFiches)}
							>
								Imprimer
							</button>
						)}
						{isBoss && !isSVP && atelierFiches.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => setOpenMarge(true)}
							>
								Marge
							</button>
						)}
						{isBoss && atelierFiches.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => setOpenTotal(true)}
							>
								Totaux
							</button>
						)}
						<button
							type="button"
							style={gs.btnPrimary}
							onClick={() => history.push("/atelier/rechercher")}
						>
							Modifier la recherche
						</button>
					</div>
				</div>

				{sortedFiches.length > 0 ? (
					<div style={tableStyles.container}>
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={tableStyles.th}>N°</th>
									<SortableTh
										label="Date"
										colKey="date"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Terminés le"
										colKey="termine"
										sort={sort}
										onSort={onSort}
									/>
									{!isSVP && (
										<SortableTh
											label="Nom"
											colKey="nom"
											sort={sort}
											onSort={onSort}
										/>
									)}
									<th style={tableStyles.th}>Marque</th>
									{isSVP && (
										<th style={tableStyles.th}>Modèle</th>
									)}
									<th style={tableStyles.th}>Plaques</th>
									<SortableTh
										label="État"
										colKey="etat"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Mécanicien"
										colKey="employe"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Montant"
										colKey="total"
										sort={sort}
										onSort={onSort}
										right
									/>
									<th
										style={{
											...tableStyles.th,
											textAlign: "right",
										}}
									>
										Outil
									</th>
								</tr>
							</thead>
							<tbody>
								{sortedFiches.map((fiche, index) => (
									<ResultRow
										key={`ficheAtelier${fiche.id}`}
										fiche={fiche}
										index={index}
										isSVP={isSVP}
										peutSupprimer={peutSupprimer}
										total={getTotal(fiche)}
										isOpen={openAtelierId === fiche.id}
										lockedBy={lockedBy(fiche.id)}
										onDoubleClick={() =>
											openFiche(fiche.id)
										}
										onOpenDep={() =>
											history.push(
												`/depannage/fiche/${fiche.dep}`,
											)
										}
										onOpenLoc={() =>
											history.push(
												`/location/fiche/${fiche.location}`,
											)
										}
										onDelete={() =>
											openDelete({
												id: fiche.id,
												title: `Atelier du ${fiche.date}`,
											})
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div style={tableStyles.container}>
						<div style={styles.empty}>Aucune fiche trouvée</div>
					</div>
				)}
			</div>

			{openTotal && (
				<AtelierSearchTotal
					visible={openTotal}
					closeModal={() => setOpenTotal(false)}
					employes={employes}
					ateliers={sortedFiches}
				/>
			)}
			{openMarge && (
				<AtelierSearchMarge
					visible={openMarge}
					closeModal={() => setOpenMarge(false)}
					ateliers={sortedFiches}
					token={token}
				/>
			)}
			{modalDelete && (
				<ResultDeleteModal
					label={modalDelete.title}
					status={deleteState}
					onConfirm={doDelete}
					onClose={closeDelete}
				/>
			)}
		</div>
	)
}

const styles = {
	shell: {
		width: "100%",
		maxWidth: 1400,
		margin: "0 auto",
	},
	headerActions: {
		display: "flex",
		gap: 10,
		alignItems: "center",
	},
	thInner: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
	},
	sortBtn: (active, asc) => ({
		border: "none",
		background: "transparent",
		padding: 2,
		cursor: "pointer",
		fontSize: 11,
		color: active ? COLORS.text : COLORS.textLight,
		opacity: active ? 1 : 0.4,
		transform: active && asc ? "rotate(180deg)" : "none",
		transition: "transform 0.15s, opacity 0.12s, color 0.12s",
		lineHeight: 1,
	}),
	td: {
		...tableStyles.td,
		fontVariantNumeric: "tabular-nums",
	},
	toolsCell: {
		display: "flex",
		gap: 6,
		justifyContent: "flex-end",
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

export default AtelierResultatsV2
